/**
 * Service Issues: fetch queue, counts, and transition mutations.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';

const QUERY_KEY = 'service-issues';
const COUNTS_KEY = 'service-issue-counts';

export interface ServiceIssue {
  id: string;
  property_id: string;
  property_name: string;
  issue_type: string;
  title: string;
  description: string | null;
  severity: string;
  source: string;
  status: string;
  resolution_type: string | null;
  resolution_notes: string | null;
  scheduled_for: string | null;
  assigned_to: string | null;
  trigger_snapshot: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  created_by: string | null;
  homeowner_reported_category: string | null;
  photo_paths: string[];
  recommendation_id: string | null;
}

export interface ServiceIssueCounts {
  detected: number;
  reviewed: number;
  scheduled: number;
  in_progress: number;
  resolved: number;
  dismissed: number;
}

interface IssueFilters {
  status?: string;
  severity?: string;
  propertyId?: string;
  limit?: number;
}

async function fetchIssues(orgId: string | null, filters: IssueFilters): Promise<ServiceIssue[]> {
  if (!orgId) return [];
  const { data, error } = await supabase.rpc('get_service_issues', {
    p_org_id: orgId,
    p_status: filters.status ?? null,
    p_severity: filters.severity ?? null,
    p_property_id: filters.propertyId ?? null,
    p_limit: filters.limit ?? 50,
    p_offset: 0,
  });
  if (error) throw error;
  return (data ?? []) as ServiceIssue[];
}

async function fetchCounts(orgId: string | null): Promise<ServiceIssueCounts> {
  const empty: ServiceIssueCounts = { detected: 0, reviewed: 0, scheduled: 0, in_progress: 0, resolved: 0, dismissed: 0 };
  if (!orgId) return empty;
  const { data, error } = await supabase.rpc('get_service_issue_counts', { p_org_id: orgId });
  if (error) throw error;
  const counts = { ...empty };
  for (const row of (data ?? []) as { status: string; count: number }[]) {
    if (row.status in counts) {
      counts[row.status as keyof ServiceIssueCounts] = Number(row.count);
    }
  }
  return counts;
}

export function useServiceIssues(filters: IssueFilters = {}) {
  const { organization } = useApp();
  const orgId = organization?.id ?? null;
  const query = useQuery({
    queryKey: [QUERY_KEY, orgId, filters],
    queryFn: () => fetchIssues(orgId, filters),
    enabled: Boolean(orgId),
  });
  return {
    issues: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useServiceIssueCounts() {
  const { organization } = useApp();
  const orgId = organization?.id ?? null;
  const query = useQuery({
    queryKey: [COUNTS_KEY, orgId],
    queryFn: () => fetchCounts(orgId),
    enabled: Boolean(orgId),
  });
  return {
    counts: query.data ?? { detected: 0, reviewed: 0, scheduled: 0, in_progress: 0, resolved: 0, dismissed: 0 },
    isLoading: query.isLoading,
  };
}

export interface CreateIssueFromRecommendationParams {
  propertyId: string;
  organizationId: string;
  recommendationId: string;
  issueType: string;
  severity: string;
  title: string;
  description: string;
  triggerSnapshot: Record<string, unknown>;
}

export function useCreateIssueFromRecommendation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreateIssueFromRecommendationParams) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('service_issues')
        .insert({
          organization_id: params.organizationId,
          property_id: params.propertyId,
          recommendation_id: params.recommendationId,
          issue_type: params.issueType,
          severity: params.severity,
          title: params.title,
          description: params.description,
          source: 'manual',
          status: 'detected',
          trigger_snapshot: params.triggerSnapshot,
          created_by: user.id,
        })
        .select('id')
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [COUNTS_KEY] });
    },
  });
}

export type TransitionParams = {
  issueId: string;
  newStatus: string;
  resolutionType?: string;
  resolutionNotes?: string;
  scheduledFor?: string;
  customerNotes?: string;
};

export function useTransitionIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: TransitionParams) => {
      const { data, error } = await supabase.rpc('transition_service_issue', {
        p_issue_id: params.issueId,
        p_new_status: params.newStatus,
        p_resolution_type: params.resolutionType ?? null,
        p_resolution_notes: params.resolutionNotes ?? null,
        p_scheduled_for: params.scheduledFor ?? null,
      });
      if (error) throw error;

      // Fire homeowner status-change notification (fire-and-forget)
      supabase.functions.invoke('notify-customer-issue', {
        body: {
          issue_id: params.issueId,
          notification_type: 'status_change',
          new_status: params.newStatus,
          customer_notes: params.customerNotes ?? null,
        },
      }).catch((e) => console.error('[useTransitionIssue] notify-customer-issue error:', e));

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [COUNTS_KEY] });
    },
  });
}
